import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "../Logo";

const NAV = [
  { to: "/", label: "Accueil", end: true },
  { to: "/services", label: "Services" },
  { to: "/realisations", label: "Nos réalisations" },
  { to: "/pourquoi-nous", label: "Pourquoi nous ?" },
  { to: "/a-propos", label: "À propos" },
  { to: "/contact", label: "Contact" },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Transparent at top of page — solid white with shadow when scrolled
  const headerBg = scrolled
    ? "bg-white shadow-[0_2px_20px_-8px_rgba(13,43,69,0.15)]"
    : "bg-transparent";
  const menuTextColor = scrolled ? "#0D2B45" : "#FFFFFF";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg}`}
      data-testid="site-header"
    >
      <div className="nx-container flex items-center" style={{ height: 100, gap: 24 }}>
        {/* LEFT — Official logo */}
        <div className="flex items-center shrink-0">
          <Logo variant="full" height={56} />
        </div>

        {/* CENTER — Menu (flex-1 fills space, items centered) */}
        <nav
          className="hidden lg:flex items-center justify-center flex-1"
          style={{ gap: 32 }}
          aria-label="Navigation principale"
        >
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className="transition-colors duration-200 hover:!text-cyan"
              style={({ isActive }) => ({
                fontFamily: "Exo 2, sans-serif",
                fontWeight: 500,
                fontSize: 16,
                whiteSpace: "nowrap",
                color: isActive ? "#00C8FF" : menuTextColor,
              })}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* RIGHT — CTA */}
        <div className="hidden lg:flex items-center shrink-0">
          <Link
            to="/contact"
            className="inline-flex items-center transition-colors duration-200"
            style={{
              gap: 12,
              height: 54,
              padding: "0 28px",
              borderRadius: 6,
              background: "#00C8FF",
              color: "#FFFFFF",
              fontFamily: "Exo 2, sans-serif",
              fontWeight: 600,
              fontSize: 13,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#0099CC"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#00C8FF"; }}
            data-testid="header-cta-devis"
          >
            Demander un devis
            <ArrowRight size={16} strokeWidth={2.25} />
          </Link>
        </div>

        {/* Mobile trigger */}
        <button
          className="lg:hidden p-2 ml-auto"
          aria-label="Ouvrir le menu"
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open"
          style={{ color: menuTextColor }}
        >
          <Menu size={26} strokeWidth={1.75} />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white flex flex-col" data-testid="mobile-menu">
          <div className="nx-container flex items-center justify-between" style={{ height: 100 }}>
            <Logo variant="full" height={52} />
            <button
              onClick={() => setOpen(false)}
              className="p-2"
              aria-label="Fermer le menu"
              data-testid="mobile-menu-close"
              style={{ color: "#0D2B45" }}
            >
              <X size={26} strokeWidth={1.75} />
            </button>
          </div>
          <nav className="nx-container flex flex-col gap-6" style={{ paddingTop: 32 }}>
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                style={({ isActive }) => ({
                  fontFamily: "Exo 2, sans-serif",
                  fontWeight: 500,
                  fontSize: 22,
                  color: isActive ? "#00C8FF" : "#0D2B45",
                })}
              >
                {n.label}
              </NavLink>
            ))}
            <div
              style={{
                marginTop: 40,
                paddingTop: 32,
                borderTop: "1px solid rgba(13,43,69,0.1)",
              }}
            >
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 12,
                  width: "100%",
                  height: 56,
                  padding: "0 28px",
                  borderRadius: 6,
                  background: "#00C8FF",
                  color: "#FFFFFF",
                  fontFamily: "Exo 2, sans-serif",
                  fontWeight: 600,
                  fontSize: 13,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                }}
                data-testid="mobile-menu-cta-devis"
              >
                Demander un devis
                <ArrowRight size={16} strokeWidth={2.25} />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
